/**
 * ReadingPage — reading-module player (EPIC-08-03).
 *
 * Route: /app/reading/:moduleId?enrollmentId=<id>
 *
 * Loads the module's document text, lets the agent read it, and records
 * completion (POST /module-progress) which unlocks the next module.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import styles from './ReadingPage.module.css';

interface ReadingContent {
  module_id: string;
  title: string;
  filename: string;
  content: string;
}

export function ReadingPage(): JSX.Element {
  const { moduleId } = useParams<{ moduleId: string }>();
  const [searchParams] = useSearchParams();
  const enrollmentId = searchParams.get('enrollmentId') ?? '';
  const { t } = useTranslation('training');
  const navigate = useNavigate();

  const [reading, setReading] = useState<ReadingContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!moduleId || !enrollmentId) {
      setError('missing_params');
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    async function load(): Promise<void> {
      try {
        const res = await apiClient.get<{ data: ReadingContent }>(
          `/reading/${moduleId}`,
          { params: { enrollment_id: enrollmentId } },
        );
        if (!cancelled) setReading(res.data.data);
      } catch {
        if (!cancelled) setError('load_failed');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [moduleId, enrollmentId]);

  async function handleComplete(): Promise<void> {
    if (!moduleId || !enrollmentId || completing) return;
    setCompleting(true);
    try {
      await apiClient.post('/module-progress', {
        enrollment_id: enrollmentId,
        module_id: moduleId,
        score: 100,
      });
      navigate(`/learn/${enrollmentId}`);
    } catch {
      setError('complete_failed');
      setCompleting(false);
    }
  }

  if (isLoading) {
    return (
      <main className={styles.page}>
        <p className={styles.loading}>{t('common.loading', 'Loading…')}</p>
      </main>
    );
  }

  if (error || !reading) {
    return (
      <main className={styles.page}>
        <button className={styles.backLink} onClick={() => navigate(-1)}>
          ← {t('common.back', 'Back')}
        </button>
        <p className={styles.error}>
          {t('reading.loadError', 'Could not load this reading. Please try again.')}
        </p>
      </main>
    );
  }

  // Split the document text into paragraphs for readable rendering.
  const paragraphs = reading.content.split('\n').filter((line) => line.trim().length > 0);

  return (
    <main className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate(`/learn/${enrollmentId}`)}>
        ← {t('common.back', 'Back to course')}
      </button>

      <header className={styles.header}>
        <span className={styles.kicker}>📖 {t('reading.kicker', 'Reading')}</span>
        <h1 className={styles.title}>{reading.title}</h1>
        <span className={styles.filename}>{reading.filename}</span>
      </header>

      <article className={styles.document}>
        {paragraphs.map((line, i) => (
          <p key={i} className={styles.paragraph}>{line}</p>
        ))}
      </article>

      <footer className={styles.footer}>
        <button
          className={styles.completeBtn}
          onClick={() => void handleComplete()}
          disabled={completing}
        >
          {completing
            ? t('reading.completing', 'Saving…')
            : t('reading.markComplete', 'Mark as complete')}
        </button>
      </footer>
    </main>
  );
}
